import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Sparkles, Save, AlertTriangle, Pencil } from "lucide-react";
import { inputCls } from "./shared";

const EditMemberDialog = ({
  edit, closeEdit, editForm, setEditForm, dirty, saving, saveEdit, generateCodeInEdit, commission,
  discardOpen, setDiscardOpen, confirmDiscard,
}) => (
  <>
    <Dialog open={edit.open} onOpenChange={closeEdit}>
      <DialogContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)] rounded-none" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Pencil className="w-5 h-5 text-[var(--ir-orange)]" />Edit Account</DialogTitle>
          <DialogDescription className="text-[var(--ir-muted)]">Edit details, then click Save to commit all changes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2 max-h-[62vh] overflow-y-auto pr-1">
          <div><Label className="text-[var(--ir-text)]">Full name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="edit-name-input" /></div>
          <div><Label className="text-[var(--ir-text)]">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="edit-email-input" /></div>
          <div><Label className="text-[var(--ir-text)]">Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="edit-phone-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[var(--ir-text)]">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)]">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--ir-text)]">Affiliate</Label>
              <Select value={editForm.is_affiliate ? "yes" : "no"} onValueChange={(v) => setEditForm({ ...editForm, is_affiliate: v === "yes" })}>
                <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="edit-affiliate-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)]">
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {editForm.is_affiliate && (
            <div className="grid grid-cols-2 gap-4 border-t border-[var(--ir-border)] pt-4" data-testid="edit-affiliate-fields">
              <div>
                <Label className="text-[var(--ir-text)]">Commission %</Label>
                <Input type="number" value={editForm.commission_percent} onChange={(e) => setEditForm({ ...editForm, commission_percent: e.target.value })} placeholder={`Default ${commission.percent}%`} className={`mt-2 ${inputCls}`} data-testid="edit-commission-input" />
                <p className="text-[var(--ir-muted)] text-xs mt-1">Overrides the global default. Won't recalculate past referrals.</p>
              </div>
              <div>
                <Label className="text-[var(--ir-text)]">Referral / Promo code <span className="text-[var(--ir-muted)]">(optional)</span></Label>
                <Input value={editForm.promo_code} onChange={(e) => setEditForm({ ...editForm, promo_code: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="edit-promo-input" />
              </div>
            </div>
          )}
          <div className="border-t border-[var(--ir-border)] pt-4">
            <Label className="text-[var(--ir-text)]">Access code (10 digits)</Label>
            <div className="flex gap-2 mt-2">
              <Input value={editForm.access_code} onChange={(e) => setEditForm({ ...editForm, access_code: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" className={`font-mono tracking-[0.15em] ${inputCls}`} data-testid="edit-code-input" />
              <Button variant="outline" onClick={generateCodeInEdit} className="border-[var(--ir-border)] bg-white text-[var(--ir-blue)] hover:bg-[var(--ir-surface2)] rounded-none whitespace-nowrap" data-testid="edit-generate-code-btn"><Sparkles className="w-4 h-4 mr-1" />Generate</Button>
            </div>
            <label className="flex items-center gap-3 cursor-pointer mt-3">
              <Checkbox checked={editForm.code_active} onCheckedChange={(v) => setEditForm({ ...editForm, code_active: !!v })} disabled={!editForm.access_code} data-testid="edit-code-active-checkbox" />
              <span className="text-sm text-[var(--ir-text)]">Access code active</span>
            </label>
            <p className="text-[var(--ir-muted)] text-xs mt-2">Saving a new 10-digit code sends the member welcome email.</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {dirty && <span className="text-[var(--ir-orange)] text-xs self-center mr-auto" data-testid="edit-unsaved-indicator">Unsaved changes</span>}
          <Button variant="outline" onClick={() => closeEdit(false)} className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none">Cancel</Button>
          <Button onClick={saveEdit} disabled={saving || !dirty} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none" data-testid="save-edit-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
      <AlertDialogContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)] rounded-none" data-testid="discard-changes-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[var(--ir-orange)]" />Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--ir-muted)]">You have unsaved edits to this account. If you close now, your changes will be lost.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none mt-0" data-testid="discard-cancel-btn">Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDiscard} className="bg-rose-600 hover:bg-rose-700 text-white rounded-none" data-testid="discard-confirm-btn">Discard changes</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
);

export default EditMemberDialog;
